<!--
  WhiteLabelMark.vue

  The rendered "Powered by" mark -- the ONE place the resolved segments
  become DOM. Card: https://trello.com/c/mq26K91V, Unit 8.

  WHY A COMPONENT AND NOT THREE COPIES OF THE TEMPLATE
  ----------------------------------------------------

  `@/utils/whiteLabelMark` already guarantees the surfaces
  (StorefrontFooter, StorefrontCart) resolve the same
  ANSWER. Before Unit 8 that was enough, because the answer was one string
  and one src and each surface could bind them in two lines.

  Unit 8 makes the answer a SEQUENCE -- text, an image, more text -- and
  hand-rolling a v-for over segments in three templates is how the surfaces
  would drift again, one `alt` or one size attribute at a time. The old
  `height="25" width="25"` is exactly that failure already realised: it was
  copied into all three and squashed every non-square logo in all three.
  So the rendering lives here, once, beside the rule that produces it.

  SIZING (Jake, 2026-09-20)
  -------------------------

  Cap the HEIGHT at 25px and let the width follow the aspect ratio:

      "This would accommodate images smaller than 25px high, and also allow
      larger images without shrinking them to the point of illegibility."

  Width capping he considered and explicitly DECLINED as an edge case, so a
  very wide wordmark renders wide rather than being letterboxed. The
  intrinsic width/height ATTRIBUTES are gone -- they are what forced the
  square -- replaced by a CSS max-height with `width: auto`. `max-height`
  rather than `height` so a logo SMALLER than 25px is not upscaled into
  blur; the number is a ceiling, not a target.

  SECURITY (CWE-79)
  -----------------

  Every text segment is bound with `{{ }}` -- a TEXT node, never v-html,
  never an href. This component takes SEGMENTS rather than a string
  precisely so that distributor-authored text cannot carry markup into the
  page: the rule module never builds HTML, and this template never parses
  any. A `{logo}` placeholder surrounded by an injection payload can only
  ever produce text segments either side of an `<img>` element.

  The `src` is already constrained to a plain upload key by the rule
  module's `resolveLogoSrc` (a scheme-carrying value never becomes a logo
  segment at all).

  The `alt` is the mark's own flat text, which is why the rule module
  returns one: a wordmark logo with no text beside it would otherwise be an
  unlabelled image to a screen reader, and "" would be wrong because the
  image IS the attribution here, not decoration.
-->
<template>
  <component
    :is="mark.isLink ? 'a' : 'span'"
    class="wl-mark"
    :class="{ 'wl-mark--link': mark.isLink }"
    v-bind="
      mark.isLink
        ? { href: mark.href, target: '_blank', rel: 'noopener noreferrer' }
        : {}
    "
    :data-testid="testid"
  >
    <template v-for="(seg, i) in mark.segments">
      <!-- Text segments are TEXT nodes. Never v-html. -->
      <span v-if="seg.type === 'text'" :key="`t${i}`" class="wl-mark__text">{{
        seg.value
      }}</span>
      <img
        v-else
        :key="`l${i}`"
        class="wl-mark__logo"
        :src="seg.src"
        :alt="logoAlt"
        :style="logoStyle"
        :data-testid="logoTestid"
      />
    </template>
  </component>
</template>

<script>
export default {
  name: "WhiteLabelMark",
  props: {
    // The resolved object from `resolveWhiteLabelMark`. Required: this
    // component deliberately does NOT call the resolver itself, so that a
    // surface cannot accidentally render a mark for the wrong store.
    mark: {
      type: Object,
      required: true,
    },
    // Per-surface testids, so the three surfaces stay individually
    // addressable by the ui-runner (selectors are [data-testid=NAME] only).
    testid: {
      type: String,
      required: true,
    },
    logoTestid: {
      type: String,
      required: true,
    },
  },
  computed: {
    logoStyle() {
      // Height capped, width free. The cap travels with the rule so the
      // three surfaces cannot each pick a number.
      return {
        maxHeight: `${this.mark.logoMaxHeightPx}px`,
        width: "auto",
      };
    },
    logoAlt() {
      // The mark's own words when it has any. When it does not -- a bare
      // wordmark logo -- fall back to the plain attribution so the image
      // is still labelled. Empty alt would mark it decorative, and it is
      // not: it IS the attribution.
      return this.mark.text || this.mark.altText || "";
    },
  },
};
</script>

<style scoped>
.wl-mark {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.wl-mark--link {
  /* The mark reads as footer brand text, not as a button -- same
   * treatment the legal links beside it already use. */
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}
.wl-mark__logo {
  /* Belt and braces with the inline style above: if a consumer ever
   * renders this without the resolved object's cap, the stylesheet still
   * prevents the unbounded-height blow-up. `width: auto` is what
   * preserves the aspect ratio -- the intrinsic width/height attributes
   * that used to be here are what squashed non-square logos. */
  max-height: 25px;
  width: auto;
  object-fit: contain;
}
</style>
